export const PAID_LEAVES_PER_YEAR = 12;
export const MONTHLY_PAID_LEAVE_ACCRUAL = 1;
export const FINANCE_YEAR_START_MONTH = 4;
export const PROFESSIONAL_TAX_MONTHLY = 200;
/** No auto TDS when annual salary (monthly × 12) is at or below this amount. */
export const TDS_ANNUAL_THRESHOLD = 1200000;

const STIPEND_DESIGNATION_RE = /\b(trainee|traniee|intern|interns)\b/i;

/** Trainee / intern designations use stipend only — no salary breakup, PT, or TDS. */
export const isTraineeDesignation = (designation) =>
  STIPEND_DESIGNATION_RE.test(String(designation || "").trim());

/** True when salary slab applies auto TDS (above ₹12L LPA). */
export const isEligibleForAutoTds = (row = {}) =>
  toNumber(row.monthlySalary) * 12 > TDS_ANNUAL_THRESHOLD;

/**
 * TDS rate for payslip label — matches payroll (TDS ÷ pro-rated basic), not TDS ÷ gross.
 */
export const getTdsDisplayPercent = (slip = {}) => {
  const tds = toNumber(slip.tds);
  if (tds <= 0) return 0;

  const workingDays = toNumber(slip.totalWorkingDays);
  const workedDays = toNumber(slip.workedDays);
  const basicSalary = toNumber(slip.basicSalary);

  let tdsBase = basicSalary;
  if (workingDays > 0) {
    tdsBase = roundMoney(basicSalary * (workedDays / workingDays));
  }

  if (tdsBase > 0) {
    return Math.round((tds / tdsBase) * 100);
  }

  const monthly = toNumber(slip.monthlySalary);
  if (monthly > 0) {
    const fallbackBasic = roundMoney(monthly * 0.5);
    const proratedBasic =
      workingDays > 0 ? roundMoney(fallbackBasic * (workedDays / workingDays)) : fallbackBasic;
    if (proratedBasic > 0) {
      return Math.round((tds / proratedBasic) * 100);
    }
  }

  return 0;
};

/** Approved leave days taken in the sheet month (includes half-day as 0.5). */
export const getLeaveDaysTaken = (row = {}) => toNumber(row.leaveDays);

/** Paid leave consumed this month (leave taken minus unpaid leave). */
export const getPaidLeaveUtilized = (row = {}) =>
  Math.max(0, roundMoney(getLeaveDaysTaken(row) - toNumber(row.unpaidLeaveDays)));

/** Human-readable leave count for payslip labels (supports 0.5 half-day). */
export const formatLeaveCountForPayslip = (value) => {
  const n = toNumber(value);
  if (!n) return "0";
  if (n === 0.5) return "0.5 day";
  const unit = n === 1 ? "day" : "days";
  return Number.isInteger(n) ? `${n} ${unit}` : `${n} ${unit}`;
};

export const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const formatCurrency = (value) => {
  const amount = toNumber(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const getFinanceYearMonthsElapsed = (year, month) => {
  const m = Number(month);
  const monthsElapsed =
    m >= FINANCE_YEAR_START_MONTH
      ? m - FINANCE_YEAR_START_MONTH + 1
      : m + (12 - FINANCE_YEAR_START_MONTH + 1);
  return Math.min(PAID_LEAVES_PER_YEAR, Math.max(1, monthsElapsed));
};

/** Paid leaves accrued through sheet month (1/month, max 12). Future months are not counted. */
export const getAccruedPaidLeavesThroughMonth = (year, month) =>
  Math.min(
    PAID_LEAVES_PER_YEAR,
    getFinanceYearMonthsElapsed(year, month) * MONTHLY_PAID_LEAVE_ACCRUAL
  );

const resolveAccruedPaidLeavesForSheet = ({
  accruedPaidLeavesThroughMonth,
  sheetYear,
  sheetMonth,
}) => {
  if (accruedPaidLeavesThroughMonth != null && accruedPaidLeavesThroughMonth !== "") {
    return Math.min(PAID_LEAVES_PER_YEAR, Math.max(0, toNumber(accruedPaidLeavesThroughMonth)));
  }
  if (sheetYear != null && sheetMonth != null) {
    return getAccruedPaidLeavesThroughMonth(sheetYear, sheetMonth);
  }
  const now = new Date();
  return getAccruedPaidLeavesThroughMonth(now.getFullYear(), now.getMonth() + 1);
};

export const calculateGrossFromStructure = (structure = {}) => {
  const basic = toNumber(structure.basicSalary);
  const hra = toNumber(structure.hra);
  const allowances = toNumber(structure.allowances);
  const bonuses = toNumber(structure.bonuses);
  const incentives = toNumber(structure.incentives);
  const other = toNumber(structure.otherComponents);
  const componentSum = roundMoney(basic + hra + allowances + bonuses + incentives + other);
  const monthly = toNumber(structure.monthlySalary);
  return monthly > 0 ? roundMoney(monthly) : componentSum;
};

export const calculateSalaryBreakdown = (monthlySalary) => {
  const monthly = roundMoney(Math.max(0, toNumber(monthlySalary)));
  const basicSalary = roundMoney(monthly * 0.5);
  const hra = roundMoney(basicSalary * 0.4);
  const remainingAfterBasicAndHra = roundMoney(Math.max(0, monthly - basicSalary - hra));
  const conveyanceAllowance = roundMoney(Math.min(1600, remainingAfterBasicAndHra));
  const specialAllowance = roundMoney(Math.max(0, monthly - basicSalary - hra - conveyanceAllowance));
  const allowances = roundMoney(conveyanceAllowance + specialAllowance);

  return {
    monthlySalary: monthly,
    basicSalary,
    hra,
    conveyanceAllowance,
    specialAllowance,
    allowances,
    bonuses: 0,
    incentives: 0,
    otherComponents: 0,
  };
};

const buildTraineeStructureComponents = (monthlySalary) => {
  const monthly = roundMoney(Math.max(0, toNumber(monthlySalary)));
  return {
    monthlySalary: monthly,
    basicSalary: monthly,
    hra: 0,
    conveyanceAllowance: 0,
    specialAllowance: 0,
    allowances: 0,
    bonuses: 0,
    incentives: 0,
    otherComponents: 0,
  };
};

/** Salary structure components for API save — stipend-only for trainees / interns. */
export const getSalaryStructureBreakdown = (designation, monthlySalary) =>
  isTraineeDesignation(designation)
    ? buildTraineeStructureComponents(monthlySalary)
    : calculateSalaryBreakdown(monthlySalary);

/** Sheet / export “basic” column — stipend for trainees / interns, else monthly minus PT. */
export const getSheetDisplayBasicSalary = (row = {}) => {
  const monthly = Math.max(0, toNumber(row.monthlySalary));
  if (isTraineeDesignation(row.designation)) return monthly;
  return Math.max(0, monthly - toNumber(row.professionalTax));
};

/** Paid leave consumed this month, derived from stored sheet row when not explicit. */
export const derivePaidLeavesUsedThisMonth = (entry = {}) => {
  const explicit = toNumber(entry.paidLeavesThisMonth);
  if (explicit > 0) return roundMoney(explicit);
  return Math.max(0, roundMoney(toNumber(entry.leaveDays) - toNumber(entry.unpaidLeaveDays)));
};

/**
 * Keep leave days and worked days in sync when HR edits either field on the sheet.
 * Auto sheets edit unpaid leave in the UI; manual sheets edit total leave days.
 */
export const syncLeaveAndWorkedDaysForSheetEntry = ({
  workingDays,
  field,
  rawValue,
  entry = {},
  isManualPeriod = false,
}) => {
  const wd = Math.max(0, toNumber(workingDays));
  const paidLeavesThisMonth = isManualPeriod ? 0 : derivePaidLeavesUsedThisMonth(entry);

  if (field === "workedDays") {
    const workedDays = Math.max(0, roundMoney(toNumber(rawValue)));
    const unpaidLeaveDays = Math.max(0, roundMoney(wd - workedDays));
    const leaveDays = isManualPeriod
      ? unpaidLeaveDays
      : Math.max(0, roundMoney(unpaidLeaveDays + paidLeavesThisMonth));
    return { leaveDays, workedDays };
  }

  if (field === "leaveDays") {
    if (isManualPeriod) {
      const leaveDays = Math.max(0, roundMoney(toNumber(rawValue)));
      const workedDays = Math.max(0, roundMoney(wd - leaveDays));
      return { leaveDays, workedDays };
    }
    const unpaidLeaveDays = Math.max(0, roundMoney(toNumber(rawValue)));
    const leaveDays = Math.max(0, roundMoney(unpaidLeaveDays + paidLeavesThisMonth));
    const workedDays = Math.max(0, roundMoney(wd - unpaidLeaveDays));
    return { leaveDays, workedDays };
  }

  return null;
};

/** Whether professional tax applies to this sheet row (trainees never have PT). */
export const isEntryProfessionalTaxEnabled = (row = {}) => {
  if (row.professionalTaxEnabled != null && row.professionalTaxEnabled !== "") {
    return Number(row.professionalTaxEnabled) !== 0;
  }
  if (isTraineeDesignation(row.designation)) return false;
  return toNumber(row.professionalTax) > 0;
};

export const calculateSalaryEntry = ({
  monthlySalary,
  basicSalary = 0,
  accommodation = 0,
  totalWorkingDays,
  leaveDays,
  postAccrualLeaveDays = 0,
  workedDays,
  ytdPaidLeavesBeforeMonth = 0,
  accruedPaidLeavesThroughMonth,
  sheetYear,
  sheetMonth,
  tds = 0,
  professionalTax = PROFESSIONAL_TAX_MONTHLY,
  designation,
}) => {
  const isTrainee = isTraineeDesignation(designation);
  const salary = toNumber(monthlySalary);
  const workingDays = toNumber(totalWorkingDays);
  if (salary < 0) throw new Error("Salary cannot be negative.");
  if (workingDays <= 0) throw new Error("Total working days must be greater than 0.");

  const paidEligibleLeaves = Math.max(0, toNumber(leaveDays));
  const postAccrualLeaves = Math.max(0, toNumber(postAccrualLeaveDays));
  const totalLeaves = roundMoney(paidEligibleLeaves + postAccrualLeaves);
  const accrued = resolveAccruedPaidLeavesForSheet({
    accruedPaidLeavesThroughMonth,
    sheetYear,
    sheetMonth,
  });
  const ytdUsedBefore = Math.max(0, toNumber(ytdPaidLeavesBeforeMonth));
  const paidLeavesAvailable = Math.max(0, accrued - ytdUsedBefore);
  const paidLeavesThisMonth = Math.min(paidEligibleLeaves, paidLeavesAvailable);
  const unpaidLeaveDays = roundMoney(
    Math.max(0, paidEligibleLeaves - paidLeavesThisMonth) + postAccrualLeaves
  );
  const worked =
    workedDays != null && workedDays !== ""
      ? Math.max(0, toNumber(workedDays))
      : Math.max(0, workingDays - unpaidLeaveDays);

  const perDaySalary = salary / workingDays;
  const leaveDeduction = roundMoney(unpaidLeaveDays * perDaySalary);
  const pt = isTrainee
    ? 0
    : roundMoney(Math.max(0, toNumber(professionalTax, PROFESSIONAL_TAX_MONTHLY)));
  const tdsAmount = isTrainee ? 0 : roundMoney(Math.max(0, toNumber(tds)));
  const grossEarnings = roundMoney(salary);
  const totalDeduction = roundMoney(leaveDeduction + pt + tdsAmount);
  const netSalary = roundMoney(Math.max(0, grossEarnings - totalDeduction));

  return {
    monthlySalary: salary,
    basicSalary: roundMoney(basicSalary),
    accommodation: roundMoney(accommodation),
    totalWorkingDays: workingDays,
    leaveDays: totalLeaves,
    workedDays: roundMoney(worked),
    ytdPaidLeavesBeforeMonth: ytdUsedBefore,
    accruedPaidLeavesThroughMonth: roundMoney(accrued),
    paidLeavesAvailableThisMonth: roundMoney(paidLeavesAvailable),
    paidLeavesThisMonth: roundMoney(paidLeavesThisMonth),
    unpaidLeaveDays,
    leaveDeduction,
    grossEarnings,
    professionalTax: pt,
    tds: tdsAmount,
    totalDeduction,
    netSalary,
    perDaySalary: roundMoney(perDaySalary),
  };
};

export const buildSheetSummary = (entries = []) => {
  const summary = entries.reduce(
    (acc, row) => {
      acc.totalSalaries += toNumber(row.monthlySalary);
      acc.totalGross += toNumber(row.grossEarnings);
      acc.totalTds += toNumber(row.tds);
      acc.totalProfessionalTax += toNumber(row.professionalTax);
      acc.totalDeductions += toNumber(row.totalDeduction);
      acc.netPayout += toNumber(row.netSalary);
      return acc;
    },
    {
      totalSalaries: 0,
      totalGross: 0,
      totalTds: 0,
      totalProfessionalTax: 0,
      totalDeductions: 0,
      netPayout: 0,
      employeeCount: entries.length,
    }
  );

  Object.keys(summary).forEach((key) => {
    if (key !== "employeeCount") summary[key] = roundMoney(summary[key]);
  });
  return summary;
};

export const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export const getMonthLabel = (month) =>
  MONTH_OPTIONS.find((option) => option.value === Number(month))?.label || month;

/** First month automatic salary sheets are generated (inclusive). */
export const FINANCE_SHEET_START = { year: 2026, month: 5 };

/** First month employees can view/download salary slips (inclusive). */
export const SALARY_SLIP_EMPLOYEE_START = { year: 2026, month: 6 };

/** Earliest month HR can create a manual historical salary sheet (inclusive). */
export const MANUAL_SHEET_MIN = { year: 2020, month: 1 };

export const getCurrentFinancePeriod = () => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
};

export const toFinancePeriodIndex = (year, month) => Number(year) * 12 + Number(month);

export const isBeforeFinanceStart = (year, month) =>
  toFinancePeriodIndex(year, month) <
  toFinancePeriodIndex(FINANCE_SHEET_START.year, FINANCE_SHEET_START.month);

export const isBeforeManualSheetMin = (year, month) =>
  toFinancePeriodIndex(year, month) <
  toFinancePeriodIndex(MANUAL_SHEET_MIN.year, MANUAL_SHEET_MIN.month);

export const isManualFinanceSheetPeriod = (year, month) =>
  !isBeforeManualSheetMin(year, month) && isBeforeFinanceStart(year, month);

export const canCreateManualFinanceSheet = (year, month) =>
  isManualFinanceSheetPeriod(year, month) && !isFutureFinanceMonth(year, month);

export const isFutureFinanceMonth = (year, month) => {
  const current = getCurrentFinancePeriod();
  return toFinancePeriodIndex(year, month) > toFinancePeriodIndex(current.year, current.month);
};

export const canGenerateFinanceSheet = (year, month) =>
  !isBeforeFinanceStart(year, month) && !isFutureFinanceMonth(year, month);

/** Salary sheet delete/clear is only allowed for manual periods before FINANCE_SHEET_START. */
export const canDeleteSalarySheetForPeriod = (year, month) =>
  isBeforeFinanceStart(year, month);

export const clampFinancePeriod = (year, month) => {
  const y = Number(year);
  const m = Number(month);
  const current = getCurrentFinancePeriod();
  if (isBeforeFinanceStart(y, m)) return { ...FINANCE_SHEET_START };
  if (isFutureFinanceMonth(y, m)) return { ...current };
  return { year: y, month: m };
};

export const getDefaultFinancePeriod = () => {
  const current = getCurrentFinancePeriod();
  return clampFinancePeriod(current.year, current.month);
};

export const getFinanceYearOptions = () => {
  const { year: maxYear } = getCurrentFinancePeriod();
  const years = [];
  for (let y = MANUAL_SHEET_MIN.year; y <= maxYear; y += 1) {
    years.push({ value: y, label: String(y) });
  }
  return years;
};

export const getFinanceMonthOptions = (year) => {
  const y = Number(year);
  const { year: maxYear, month: maxMonth } = getCurrentFinancePeriod();
  if (y < MANUAL_SHEET_MIN.year || y > maxYear) return [];

  const startMonth = y === MANUAL_SHEET_MIN.year ? MANUAL_SHEET_MIN.month : 1;
  const endMonth = y === maxYear ? maxMonth : 12;

  return MONTH_OPTIONS.filter((option) => option.value >= startMonth && option.value <= endMonth);
};

export const isBeforeEmployeeSalarySlipStart = (year, month) =>
  toFinancePeriodIndex(year, month) <
  toFinancePeriodIndex(SALARY_SLIP_EMPLOYEE_START.year, SALARY_SLIP_EMPLOYEE_START.month);

export const getEmployeeSalarySlipStartLabel = () =>
  `${getMonthLabel(SALARY_SLIP_EMPLOYEE_START.month)} ${SALARY_SLIP_EMPLOYEE_START.year}`;

export const clampEmployeeSalarySlipPeriod = (year, month) => {
  const y = Number(year);
  const m = Number(month);
  const current = getCurrentFinancePeriod();
  if (isBeforeEmployeeSalarySlipStart(y, m)) return { ...SALARY_SLIP_EMPLOYEE_START };
  if (isFutureFinanceMonth(y, m)) return { ...current };
  return { year: y, month: m };
};

export const getDefaultEmployeeSalarySlipPeriod = () => {
  const current = getCurrentFinancePeriod();
  return clampEmployeeSalarySlipPeriod(current.year, current.month);
};

export const getEmployeeSalarySlipYearOptions = () => {
  const { year: maxYear } = getCurrentFinancePeriod();
  const years = [];
  for (let y = SALARY_SLIP_EMPLOYEE_START.year; y <= maxYear; y += 1) {
    years.push({ value: y, label: String(y) });
  }
  return years;
};

export const getEmployeeSalarySlipMonthOptions = (year) => {
  const y = Number(year);
  const { year: maxYear, month: maxMonth } = getCurrentFinancePeriod();
  if (y < SALARY_SLIP_EMPLOYEE_START.year || y > maxYear) return [];

  const startMonth =
    y === SALARY_SLIP_EMPLOYEE_START.year ? SALARY_SLIP_EMPLOYEE_START.month : 1;
  const endMonth = y === maxYear ? maxMonth : 12;

  return MONTH_OPTIONS.filter((option) => option.value >= startMonth && option.value <= endMonth);
};

export const getFinancePeriodStatus = (year, month) => {
  if (isBeforeManualSheetMin(year, month)) return "too_early";
  if (isFutureFinanceMonth(year, month)) return "future";
  if (isManualFinanceSheetPeriod(year, month)) return "manual";
  return "ok";
};

export const SALARY_ENTRY_STATUS = {
  PENDING: "pending",
  PAID: "paid",
  ON_HOLD: "onhold",
};

export const SALARY_ENTRY_STATUS_OPTIONS = [
  { value: SALARY_ENTRY_STATUS.PENDING, label: "Pending" },
  { value: SALARY_ENTRY_STATUS.PAID, label: "Paid" },
  { value: SALARY_ENTRY_STATUS.ON_HOLD, label: "On Hold" },
];

/** Maps legacy draft/hold values to current status codes. */
export const normalizeSalaryEntryStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "draft") return SALARY_ENTRY_STATUS.PENDING;
  if (value === "hold") return SALARY_ENTRY_STATUS.ON_HOLD;
  return value;
};

export const getSalaryEntryStatusLabel = (status) => {
  const normalized = normalizeSalaryEntryStatus(status);
  return (
    SALARY_ENTRY_STATUS_OPTIONS.find((option) => option.value === normalized)?.label ||
    status ||
    "—"
  );
};

export const isSalaryEntryPaid = (status) =>
  normalizeSalaryEntryStatus(status) === SALARY_ENTRY_STATUS.PAID;

/** pending | paid | onhold — for status badge / select styling */
export const getSalaryEntryStatusVariant = (status) => {
  const normalized = normalizeSalaryEntryStatus(status);
  if (normalized === SALARY_ENTRY_STATUS.PAID) return "paid";
  if (normalized === SALARY_ENTRY_STATUS.ON_HOLD) return "onhold";
  return "pending";
};

export const getSalaryEntryStatusBadgeClass = (status) =>
  `salary-entry-status salary-entry-status--${getSalaryEntryStatusVariant(status)}`;

export const getSalaryEntryStatusSelectClass = (status) =>
  `salary-entry-status-select salary-entry-status-select--${getSalaryEntryStatusVariant(status)}`;

/** Count Mon–Fri days in a calendar month (weekends excluded). Month is 1–12. */
export const getWorkingDaysInCalendarMonth = (year, month) => {
  const y = Number(year);
  const m = Number(month);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return 0;

  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0);
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count += 1;
    current.setDate(current.getDate() + 1);
  }
  return count;
};
